import type { ObservabilityCompareDimension, ObservabilityCompareMetric } from '@scatterin/api-types';
import { ArrowUpRight, ChevronDown, Gauge, Lightbulb, TriangleAlert } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import { DataPanel } from '@/components/dataPanel';
import { EmptyState, StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectGroup } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { useCompareQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatDuration, formatInteger, formatTime } from '@/lib/format';
import { drillThroughSearch } from '@/lib/navigation';
import { compareMetricValues } from '@/lib/urlState';

const labels: Record<string, string> = {
  analysis_type: 'workflow',
  client_errors: 'browser errors',
  failures: 'failure volume',
  job_kind: 'job kind',
  metric_family: 'metric family',
  metric_name: 'metric name',
  metric_sample_value: 'published metric values',
  page_load: 'page load duration',
  queue_wait_duration: 'queue wait duration',
  route_navigation: 'route navigation duration',
  run_duration: 'run duration',
  runs: 'run volume',
  runtime_type: 'runtime',
  service_name: 'service',
  source: 'source',
  span_duration: 'span duration',
  span_name: 'span',
  startup_duration: 'startup duration',
  status: 'status',
  web_vital_name: 'Web Vital',
  web_vital_value: 'Web Vital value',
};

const labelFor = (value: string): string => labels[value] ?? value.replaceAll('_', ' ');

const compatibleDimensions: Record<ObservabilityCompareMetric, ObservabilityCompareDimension[]> = {
  client_errors: ['route', 'source'],
  failures: ['service_name', 'analysis_type', 'runtime_type', 'source', 'status'],
  metric_sample_value: ['metric_name', 'metric_family', 'source'],
  page_load: ['route', 'source'],
  queue_wait_duration: ['analysis_type', 'service_name', 'runtime_type', 'source', 'job_kind', 'status'],
  route_navigation: ['route', 'source'],
  run_duration: ['analysis_type', 'service_name', 'runtime_type', 'source', 'job_kind', 'status'],
  runs: ['analysis_type', 'service_name', 'runtime_type', 'source', 'job_kind', 'status'],
  span_duration: ['span_name', 'analysis_type', 'service_name', 'runtime_type', 'source'],
  startup_duration: ['runtime_type', 'service_name', 'analysis_type', 'source', 'job_kind', 'status'],
  web_vital_value: ['web_vital_name', 'route', 'source'],
};

const presets: {
  description: string;
  dimension: ObservabilityCompareDimension;
  label: string;
  metric: ObservabilityCompareMetric;
}[] = [
  { description: 'P95 end-to-end duration by workflow.', dimension: 'analysis_type', label: 'Slowest workflows', metric: 'run_duration' },
  {
    description: 'Recorded failures grouped by service.',
    dimension: 'service_name',
    label: 'Failure volume by service',
    metric: 'failures',
  },
  {
    description: 'P95 queue delay by workflow.',
    dimension: 'analysis_type',
    label: 'Queue wait by workflow',
    metric: 'queue_wait_duration',
  },
  { description: 'P95 startup evidence by runtime.', dimension: 'runtime_type', label: 'Startup by runtime', metric: 'startup_duration' },
  {
    description: 'Recorded client errors grouped by route.',
    dimension: 'route',
    label: 'Browser errors by route',
    metric: 'client_errors',
  },
];

const resultValue = (
  bucket: { count?: number; values?: { p50?: number | null; p95?: number | null; p99?: number | null; unit: string } | null },
  percentile: 'p50' | 'p95' | 'p99',
): number => bucket.values?.[percentile] ?? bucket.count ?? 0;

const resultLabel = (
  bucket: { count?: number; values?: { p50?: number | null; p95?: number | null; p99?: number | null; unit: string } | null },
  percentile: 'p50' | 'p95' | 'p99',
): string => (bucket.values ? formatDuration(bucket.values[percentile], bucket.values.unit) : formatInteger(bucket.count));

const resultDestination = (metric: ObservabilityCompareMetric): string => {
  if (metric === 'failures') return '/failures';
  if (['client_errors', 'page_load', 'route_navigation', 'web_vital_value'].includes(metric)) return '/frontend-experience';
  return '/performance';
};

export const QueryPage = () => {
  const { setState, state } = useRouteContext();
  const compareQuery = useCompareQuery(state);
  const location = useLocation();
  const buckets = compareQuery.data?.buckets ?? [];
  const dimensions = compatibleDimensions[state.compareMetric];
  const totalSamples = buckets.reduce((total, bucket) => total + (bucket.count ?? 0), 0);
  const maximum = Math.max(...buckets.map((bucket) => resultValue(bucket, state.percentile)), 0);
  const strongest = buckets.toSorted((left, right) => resultValue(right, state.percentile) - resultValue(left, state.percentile))[0];
  const definitionSearch = new URLSearchParams({ search: state.compareMetric, view: 'definitions' });

  const applyQuery = (metric: ObservabilityCompareMetric, dimension: ObservabilityCompareDimension) => {
    setState({ compareDimension: dimension, compareMetric: metric });
  };

  return (
    <DataPanel
      error={compareQuery.error}
      isError={compareQuery.isError}
      isLoading={compareQuery.isLoading}
      loadingLabel="Loading compare query"
      onRetry={() => void compareQuery.refetch()}
    >
      <div className="grid min-w-0 gap-5">
        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {presets.map((preset) => (
            <button
              className="border-line bg-panel hover:bg-selection focus-visible:ring-focus grid min-h-28 gap-2 rounded-lg border p-4 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
              key={preset.label}
              onClick={() => applyQuery(preset.metric, preset.dimension)}
              type="button"
            >
              <span className="text-ink text-sm font-semibold">{preset.label}</span>
              <span className="text-muted text-xs">{preset.description}</span>
            </button>
          ))}
        </section>

        <Card>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-muted text-xs font-semibold">Current question</p>
                <p className="text-ink mt-1 text-lg font-semibold">
                  Compare {labelFor(state.compareMetric)} by {labelFor(state.compareDimension)}.
                </p>
              </div>
              <Badge tone="blue">{state.env}</Badge>
            </div>
            <details className="border-line rounded-md border">
              <summary className="text-ink flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm font-semibold">
                Custom query builder
                <ChevronDown aria-hidden="true" className="size-4" />
              </summary>
              <div className="border-line border-t p-4">
                <SelectGroup>
                  <Select
                    label="Metric"
                    onValueChange={(value) => {
                      const metric = value as ObservabilityCompareMetric;
                      applyQuery(metric, compatibleDimensions[metric][0]);
                    }}
                    options={compareMetricValues.map((value) => ({ label: labelFor(value), value }))}
                    value={state.compareMetric}
                  />
                  <Select
                    label="Dimension"
                    onValueChange={(value) => setState({ compareDimension: value as ObservabilityCompareDimension })}
                    options={dimensions.map((value) => ({ label: labelFor(value), value }))}
                    value={dimensions.includes(state.compareDimension) ? state.compareDimension : dimensions[0]}
                  />
                </SelectGroup>
              </div>
            </details>
          </CardContent>
        </Card>

        {buckets.length === 0 ? (
          <EmptyState title="No evidence matched this comparison" tone="filtered">
            <div className="grid justify-items-center gap-3">
              <p>Try a broader period or a high-value preset supported by this metric.</p>
              <Button onClick={() => applyQuery('runs', 'analysis_type')} variant="secondary">
                <Lightbulb aria-hidden="true" className="size-4" />
                Compare run volume by workflow
              </Button>
            </div>
          </EmptyState>
        ) : null}

        {buckets.length > 0 ? (
          <>
            <StateView
              title={
                strongest
                  ? `${strongest.key} leads ${labelFor(state.compareMetric)} at ${resultLabel(strongest, state.percentile)}`
                  : `${formatInteger(totalSamples)} samples matched`
              }
              tone="healthy"
            >
              {formatInteger(totalSamples)} samples across {formatInteger(buckets.length)} {labelFor(state.compareDimension)} groups. Window
              ends {formatTime(compareQuery.data?.filter.endTime)}.
            </StateView>

            <Card>
              <CardHeader className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>{labelFor(state.compareMetric)} comparison</CardTitle>
                  <p className="text-muted mt-1 text-xs">
                    Bars use {buckets[0]?.values ? state.percentile.toUpperCase() : 'recorded count'}.
                  </p>
                </div>
                <Button asChild variant="ghost">
                  <Link to={{ pathname: '/system', search: definitionSearch.toString() }}>
                    Metric definition
                    <ArrowUpRight aria-hidden="true" className="size-4" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                <ol
                  className="grid gap-3"
                  role="img"
                  aria-label={`${labelFor(state.compareMetric)} by ${labelFor(state.compareDimension)}`}
                >
                  {buckets.map((bucket) => (
                    <li className="grid gap-1.5" key={bucket.key}>
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-ink truncate font-medium">{bucket.key}</span>
                        <span className="text-muted numeric shrink-0">{resultLabel(bucket, state.percentile)}</span>
                      </div>
                      <div className="bg-line h-2 overflow-hidden rounded-full">
                        <div
                          className="bg-chart-secondary h-full rounded-full"
                          style={{ width: `${maximum ? Math.max((resultValue(bucket, state.percentile) / maximum) * 100, 2) : 0}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            <TableShell>
              <Table aria-label="Compare result data" className="table-fixed" style={{ minWidth: '48rem' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{labelFor(state.compareDimension)}</TableHead>
                    <TableHead className="w-28">Samples</TableHead>
                    <TableHead className="w-36">P50</TableHead>
                    <TableHead className="w-36">P95</TableHead>
                    <TableHead className="w-36">P99</TableHead>
                    <TableHead className="w-32 text-right">Investigate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {buckets.map((bucket) => (
                    <TableRow key={bucket.key}>
                      <TableCell className="font-semibold">{bucket.key}</TableCell>
                      <TableCell className="numeric">{formatInteger(bucket.count)}</TableCell>
                      <TableCell>{bucket.values ? formatDuration(bucket.values.p50, bucket.values.unit) : 'Count metric'}</TableCell>
                      <TableCell>{bucket.values ? formatDuration(bucket.values.p95, bucket.values.unit) : 'Count metric'}</TableCell>
                      <TableCell>{bucket.values ? formatDuration(bucket.values.p99, bucket.values.unit) : 'Count metric'}</TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost">
                          <Link
                            to={{
                              pathname: resultDestination(state.compareMetric),
                              search: drillThroughSearch(location.search, {
                                analysisTypes: state.compareDimension === 'analysis_type' ? [bucket.key] : state.analysisTypes,
                                search: ['route', 'metric_name', 'span_name'].includes(state.compareDimension) ? bucket.key : undefined,
                                serviceNames: state.compareDimension === 'service_name' ? [bucket.key] : state.serviceNames,
                              }),
                            }}
                          >
                            Open
                            <ArrowUpRight aria-hidden="true" className="size-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          </>
        ) : null}

        <div className="text-muted flex items-center gap-2 text-xs">
          {compareQuery.data?.buckets?.length ? (
            <Gauge aria-hidden="true" className="size-4" />
          ) : (
            <TriangleAlert aria-hidden="true" className="size-4" />
          )}
          Query scope is bounded to {state.env} and the selected date range; results are deterministic recorded evidence.
        </div>
      </div>
    </DataPanel>
  );
};
